import type Alpine from "alpinejs";
import { dispatchToast } from "./htmx-helpers";
import { isAnyUploadActive } from "./upload-guard";

/**
 * Build-skew guard — tabs left open across a deploy.
 *
 * hx-boost only swaps <body>; the content-hashed JS bundle (and CSS) in <head>
 * stay at whatever build the tab first loaded. After a deploy, a boosted
 * navigation swaps in NEW markup that references Alpine components / magics
 * the OLD bundle never registered → "crCounts / whatsNewDrawer / $ohSection is
 * not defined" ReferenceErrors and silently dead features until a hard reload
 * (GlitchTip STUDIO-BROWSER-11/12/17/18/19/1C).
 *
 * The server sends its build id on every response (`X-App-Build`,
 * internal/api/middleware_app_build.go) and the page renders the build it was
 * loaded with into <meta name="app-build"> (never re-rendered under boost).
 *
 * On a mismatch:
 *   - full-page navigation (boosted GET, or a GET targeting <body> such as the
 *     HX-Location follow-up) and no uploads in flight → cancel the swap and do
 *     ONE real navigation to the same URL, so <head> loads the new bundle.
 *     This is a documented exception to the "no window.location" rule
 *     (CLAUDE.md → Navigation & Refresh Responses; docs/htmx.md §Build skew).
 *   - uploads (or a wizard autosave PATCH) in flight → never navigate (Upload
 *     Resilience Invariant; an unload can abort the save); let the
 *     swap proceed and show a one-time "reload once uploads finish" toast. The
 *     NEXT navigation after the uploads finish re-evaluates and reloads.
 *   - partial fragment refreshes (hx-get into a container, polling, POSTs) →
 *     never navigate: a fragment CAN reference a new component too, but a hard
 *     reload in the middle of an interaction (open drawer, half-filled form,
 *     background poll) is far more disruptive than the rare fragment-level
 *     error. Show the one-time "new version available" toast instead; the next
 *     full navigation fixes it.
 *
 * Loop guard: at most one forced navigation per target build per tab session
 * (sessionStorage). If the reloaded page still mismatches (a cache served stale
 * HTML) we fall back to the toast. If sessionStorage is unavailable we cannot
 * guarantee that, so we never force-navigate and only toast.
 */

export type BuildSkewAction = "proceed" | "hard-navigate" | "notify";

export const APP_BUILD_HEADER = "X-App-Build";
export const APP_BUILD_META = "app-build";
const RELOADED_FOR_KEY = "studio:build-skew-reloaded-for";

/**
 * Pure decision function. `alreadyReloadedFor` is the build id this tab session
 * already force-navigated for (or null).
 */
export function buildSkewAction(
  currentBuild: string | null | undefined,
  responseBuild: string | null | undefined,
  isBoostedFullNav: boolean,
  uploadsActive: boolean,
  alreadyReloadedFor: string | null | undefined,
): BuildSkewAction {
  const cur = (currentBuild || "").trim();
  const next = (responseBuild || "").trim();
  // Unknown on either side (dev without a manifest, a response that bypassed
  // the middleware, a page predating the meta) → no signal, never act.
  if (!cur || !next || cur === next) return "proceed";
  if (!isBoostedFullNav || uploadsActive) return "notify";
  if (alreadyReloadedFor === next) return "notify";
  return "hard-navigate";
}

export function shouldHardNavigate(
  currentBuild: string | null | undefined,
  responseBuild: string | null | undefined,
  isBoostedFullNav: boolean,
  uploadsActive: boolean,
  alreadyReloadedFor: string | null | undefined,
): boolean {
  return (
    buildSkewAction(
      currentBuild,
      responseBuild,
      isBoostedFullNav,
      uploadsActive,
      alreadyReloadedFor,
    ) === "hard-navigate"
  );
}

/** Minimal shape of htmx 1.9 `htmx:beforeSwap` detail that we rely on. */
export interface SwapDetail {
  boosted?: boolean;
  target?: unknown;
  requestConfig?: { verb?: string; path?: string };
  pathInfo?: { finalRequestPath?: string; anchor?: string };
  xhr?: { status?: number; responseURL?: string; getResponseHeader(name: string): string | null };
}

/**
 * A response is a full-page navigation when it is a GET that is either boosted
 * (link/GET-form under hx-boost — the URL is a real page, even when the swap is
 * narrowed by an inherited hx-target) or targets <body> (HX-Location follow-up,
 * the htmx.ajax body swap "reload" pattern). POSTs are never re-navigated.
 */
export function isFullPageNavigation(detail: SwapDetail, body: unknown): boolean {
  const verb = (detail.requestConfig?.verb || "").toLowerCase();
  if (verb !== "get") return false;
  return detail.boosted === true || (body != null && detail.target === body);
}

/** URL to navigate to: the final (post-redirect) URL, plus the #anchor if any. */
export function navigationURL(detail: SwapDetail): string | null {
  const base =
    detail.xhr?.responseURL ||
    detail.pathInfo?.finalRequestPath ||
    detail.requestConfig?.path ||
    "";
  if (!base) return null;
  const anchor = detail.pathInfo?.anchor;
  return anchor && !base.includes("#") ? `${base}#${anchor}` : base;
}

function readReloadedFor(): string | null {
  try {
    return window.sessionStorage.getItem(RELOADED_FOR_KEY);
  } catch {
    return null;
  }
}

function markReloadedFor(build: string): boolean {
  try {
    window.sessionStorage.setItem(RELOADED_FOR_KEY, build);
    return window.sessionStorage.getItem(RELOADED_FOR_KEY) === build;
  } catch {
    return false;
  }
}

/**
 * A wizard autosave whose PATCH is still in flight (its mark reads "saving",
 * wizard-autosave.ts setMark). A hard navigation would unload the page and can
 * abort that fetch; the in-page swap it replaces never unloads. Treated like an
 * in-flight upload: swap normally and toast. The wiki editor needs no check —
 * its unload flush is a synchronous XHR.
 */
export function isAutosaveInFlight(root: ParentNode = document): boolean {
  return root.querySelector('.autosave-mark[data-state="saving"]') !== null;
}

function currentBuild(): string {
  return document.querySelector(`meta[name="${APP_BUILD_META}"]`)?.getAttribute("content") || "";
}

export function registerBuildSkewGuard(alpine: typeof Alpine) {
  if ((window as any).__studioBuildSkewGuardRegistered) return;
  (window as any).__studioBuildSkewGuardRegistered = true;

  // One toast per build per page lifetime — polling fragments must not nag.
  const notified = new Set<string>();
  const notify = (build: string, uploads: boolean) => {
    if (notified.has(build)) return;
    notified.add(build);
    const cfg = (window as any).STUDIO_CONFIG?.appUpdate || {};
    const msg = uploads
      ? cfg.available_after_uploads ||
        "A new version of Studio is available — reload the page once your uploads have finished."
      : cfg.available || "A new version of Studio is available. Reload the page to update.";
    dispatchToast(msg, uploads ? "warning" : "info");
  };

  document.addEventListener("htmx:beforeSwap", (evt: Event) => {
    const detail = (evt as CustomEvent).detail as SwapDetail & { shouldSwap?: boolean };
    const xhr = detail?.xhr;
    if (!xhr || detail.shouldSwap === false) return;
    const responseBuild = (xhr.getResponseHeader(APP_BUILD_HEADER) || "").trim();
    const cur = currentBuild();
    if (!responseBuild || !cur || responseBuild === cur) return; // fast path

    const uploads = isAnyUploadActive(alpine);
    const action = buildSkewAction(
      cur,
      responseBuild,
      isFullPageNavigation(detail, document.body),
      uploads || isAutosaveInFlight(),
      readReloadedFor(),
    );
    if (action === "hard-navigate") {
      const url = navigationURL(detail);
      // Record BEFORE navigating; if it can't be recorded we can't bound the
      // number of forced navigations, so degrade to the toast.
      if (url && markReloadedFor(responseBuild)) {
        detail.shouldSwap = false;
        // Deliberate exception to the "no window.location" rule — see header.
        window.location.assign(url);
        return;
      }
      notify(responseBuild, uploads);
      return;
    }
    if (action === "notify") notify(responseBuild, uploads);
  });

  // Browser Back/Forward: with historyCacheSize 0 htmx refetches the page via
  // its own XHR and swaps it WITHOUT firing htmx:beforeSwap. The event below is
  // not cancelable, but the address bar already shows the target URL, so a
  // reload lands on the right page with the new bundle.
  // Listen on `document`: this bundle runs from <head>, before <body> exists;
  // the event is dispatched on body and bubbles.
  document.addEventListener("htmx:historyCacheMissLoad", (evt: Event) => {
    const xhr = (evt as CustomEvent).detail?.xhr as XMLHttpRequest | undefined;
    const responseBuild = (xhr?.getResponseHeader(APP_BUILD_HEADER) || "").trim();
    const cur = currentBuild();
    const uploads = isAnyUploadActive(alpine);
    const action = buildSkewAction(
      cur,
      responseBuild,
      true,
      uploads || isAutosaveInFlight(),
      readReloadedFor(),
    );
    if (action === "hard-navigate" && markReloadedFor(responseBuild)) {
      window.location.reload();
    } else if (action !== "proceed") {
      notify(responseBuild, uploads);
    }
  });
}
